import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Card, CardContent } from '@mui/material';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import SearchField from '../../components/SearchField';
import CustomContactTable from '../../components/CustomContactTable';
import { Error } from '../../components/Error';
import ContactFormModal from '../../components/Forms/ContactFormModal';
import ContactFormFields from '../../components/Forms/ContactFormFields';
import ContactRowActions from '../../components/Forms/ContactRowActions';
import { contactCreate } from '../../redux/actions';
import api from '../../utils/functions/api';
import { validateThaiAddress } from '../../utils/functions/thaiZipCascade';
import useZipCascade from '../../hooks/useZipCascade';
import useContactList from '../../hooks/useContactList';
import { emptyValues } from './client.constants';

function Suppliers() {
  const dispatch = useDispatch();

  const {
    contact, localRows, setLocalRows, inputValue, setInputValue, size, setSize, page, setPage, refetch,
  } = useContactList('supplier');

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [addSubmitError, setAddSubmitError] = useState('');
  const [editSubmitError, setEditSubmitError] = useState('');

  const {
    handleSubmit: handleAddSubmit, control: addControl, reset: addReset, setValue: addSetValue, watch: addWatch,
  } = useForm({ defaultValues: emptyValues });
  const {
    handleSubmit: handleEditSubmit, control: editControl, reset: editReset, setValue: editSetValue, watch: editWatch,
  } = useForm({ defaultValues: emptyValues });

  const { cascade: addCascade, setZipSelections: setAddZipSelections } = useZipCascade(addWatch, addSetValue);
  const { cascade: editCascade, setZipSelections: setEditZipSelections } = useZipCascade(editWatch, editSetValue);

  const handleAddOpen = () => {
    setAddZipSelections({ province: null, district: null });
    setAddSubmitError('');
    setAddOpen(true);
  };
  const handleAddClose = () => { setAddOpen(false); addReset(emptyValues); };

  const handleEditOpen = (row) => {
    setEditingRow(row);
    editReset(row);
    // seed จากค่าที่มีอยู่เดิม ไม่งั้น cascade จะ recompute แล้วเคลียร์อำเภอ/ตำบลที่ถูกต้องอยู่แล้วทิ้ง
    setEditZipSelections({ province: row?.province || null, district: row?.amphoe || null });
    setEditSubmitError('');
    setEditOpen(true);
  };
  const handleEditClose = () => { setEditOpen(false); setEditingRow(null); editReset(emptyValues); };

  const onAddSubmit = async (data) => {
    const zipError = validateThaiAddress(data);
    if (zipError) { setAddSubmitError(zipError); return; }
    try {
      await dispatch(contactCreate({ ...data, role: 'supplier' }));
      refetch();
      handleAddClose();
    } catch (err) {
      console.error('Failed to create supplier:', err);
    }
  };

  const onEditSubmit = async (data) => {
    const zipError = validateThaiAddress(data);
    if (zipError) { setEditSubmitError(zipError); return; }
    const prevRows = localRows;
    setLocalRows((prev) => prev.map((r) => (r._id === editingRow._id ? { ...r, ...data } : r)));
    handleEditClose();
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/contact/${editingRow._id}`, data);
    } catch (err) {
      setLocalRows(prevRows);
      console.error('Failed to update supplier:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ยืนยันการลบ?')) return;
    const prevRows = localRows;
    setLocalRows((prev) => prev.filter((r) => r._id !== id));
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/contact/${id}`);
    } catch (err) {
      setLocalRows(prevRows);
      console.error('Failed to delete supplier:', err);
    }
  };

  if (!contact.isLoading && !contact.isCompleted) {
    return <Error />;
  }

  return (
    <Box>
      <ContactFormModal
        open={addOpen}
        onClose={handleAddClose}
        title="เพิ่มผู้ขาย"
        subtitle="กรอกข้อมูลผู้ขายองค์กรหรือบุคคลธรรมดา"
        onSubmit={handleAddSubmit(onAddSubmit)}
        error={addSubmitError}
      >
        <ContactFormFields control={addControl} cascade={addCascade} setZipSelections={setAddZipSelections} />
      </ContactFormModal>

      <ContactFormModal
        open={editOpen}
        onClose={handleEditClose}
        title="แก้ไขข้อมูลผู้ขาย"
        subtitle={editingRow?.name || ''}
        onSubmit={handleEditSubmit(onEditSubmit)}
        error={editSubmitError}
      >
        <ContactFormFields control={editControl} cascade={editCascade} setZipSelections={setEditZipSelections} />
      </ContactFormModal>

      <Card elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
            <SearchField
              label="ค้นหาผู้ขาย"
              placeholder="ชื่อบริษัท, ผู้ติดต่อ, อีเมล..."
              size="small"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              sx={{ width: { xs: '100%', sm: '350px' }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button variant="contained" onClick={handleAddOpen} startIcon={<AddIcon />} sx={{ borderRadius: 2, textTransform: 'none', px: 2, whiteSpace: 'nowrap' }}>
              เพิ่มผู้ขาย
            </Button>
          </Box>
        </CardContent>
      </Card>

      <CustomContactTable
        rows={localRows}
        page={page}
        size={size}
        total={contact.total || 0}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setSize(e.target.value); setPage(1); }}
        actionCell={(row) => <ContactRowActions row={row} onEdit={handleEditOpen} onDelete={handleDelete} />}
        isLoading={contact.isLoading}
      />
    </Box>
  );
}

Suppliers.propTypes = { title: PropTypes.string, subtitle: PropTypes.string };
Suppliers.defaultProps = { title: 'Supplier', subtitle: 'จัดการรายชื่อผู้จำหน่าย' };

export default Suppliers;
